import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";

import ClusterDetailsPage from "../components/ClusterDetailsPage";
import { TypedClusterDelete, TypedClusterUpdate } from "../mutations";
import { useClusterDetailsQuery } from "../queries";
import { ClusterDelete } from "../types/ClusterDelete";
import { ClusterUpdate } from "../types/ClusterUpdate";
import {
  clusterListUrl,
  clusterUrl,
  ClusterUrlDialog,
  ClusterUrlQueryParams
} from "../urls";

interface ClusterDetailsProps {
  id: string;
  params: ClusterUrlQueryParams;
}

export const ClusterDetails: React.FC<ClusterDetailsProps> = props => {
  const { id, params } = props;
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();

  const { data, loading } = useClusterDetailsQuery({
    displayLoader: true,
    variables: {
      id
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    ClusterUrlDialog,
    ClusterUrlQueryParams
  >(navigate, params => clusterUrl(id, params), params);

  const handleClusterUpdate = (data: ClusterUpdate) => {
    if (data.clusterUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully updated cluster"
        })
      });
      navigate(clusterUrl(data.clusterUpdate.cluster.id), true);
    }
  };

  const handleBannerDelete = (data: ClusterDelete) => {
    if (data.clusterDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully deleted cluster"
        })
      });
      navigate(clusterListUrl());
    } else {
      notify({
        status: "error",
        text: `${data.clusterDelete.errors[0].message}`
      });
    }
  };

  return (
    <TypedClusterDelete onCompleted={handleBannerDelete}>
      {(clusterDelete, clusterDeleteOpts) => (
        <TypedClusterUpdate onCompleted={handleClusterUpdate}>
          {(clusterUpdate, clusterOpts) => (
            <>
              <WindowTitle title={intl.formatMessage(sectionNames.cluster)} />
              <ClusterDetailsPage
                errors={clusterOpts.data?.clusterUpdate.errors || []}
                data={maybe(() => data.cluster)}
                onBack={() => navigate(clusterListUrl())}
                disabled={
                  clusterOpts.loading || loading || clusterDeleteOpts.loading
                }
                saveButtonBarState={
                  clusterOpts.status || clusterDeleteOpts.status
                }
                onDelete={() => {
                  openModal("delete");
                }}
                onSubmit={formData => {
                  clusterUpdate({
                    variables: {
                      id,
                      input: {
                        lowerPrepTimeDays: parseInt(
                          formData.lowerPrepTimeDays,
                          0
                        ),
                        lowerPrepTimeHours: parseInt(
                          formData.lowerPrepTimeHours,
                          0
                        ),
                        lowerPrepTimeMins: parseInt(
                          formData.lowerPrepTimeMins,
                          0
                        ),
                        name: formData.name,
                        productType: formData.productType,
                        upperPrepTimeDays: parseInt(
                          formData.upperPrepTimeDays,
                          0
                        ),
                        upperPrepTimeHours: parseInt(
                          formData.upperPrepTimeHours,
                          0
                        ),
                        upperPrepTimeMins: parseInt(
                          formData.upperPrepTimeMins,
                          0
                        )
                      }
                    }
                  });
                }}
              />
              <ActionDialog
                confirmButtonState={clusterDeleteOpts.status}
                onClose={closeModal}
                onConfirm={() => clusterDelete({ variables: { id } })}
                open={params.action === "delete"}
                title={intl.formatMessage({
                  defaultMessage: "Delete cluster",
                  description: "dialog header"
                })}
                confirmButtonLabel="Delete"
                variant="delete"
              >
                <DialogContentText>
                  <FormattedMessage
                    defaultMessage="Are you sure you want to delete this cluster?"
                    description="dialog content"
                  />
                </DialogContentText>
              </ActionDialog>
            </>
          )}
        </TypedClusterUpdate>
      )}
    </TypedClusterDelete>
  );
};

export default ClusterDetails;
